import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from './config';

const AcceptInvitation = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [invitation, setInvitation] = useState(null);
    const [error, setError] = useState(null);
    
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/invitation/${token}`, {
                credentials: 'include'
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Invalid or expired invitation');
            }

            const data = await response.json();
            setInvitation(data);
        } catch (err) {
            setError(err.message || 'Failed to validate invitation');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setAccepting(true);

        try {
            const response = await fetch(`${API_BASE_URL}/auth/accept-invitation/${token}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    username,
                    password
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Failed to accept invitation');
            }

            const data = await response.json();
            
            // Success! User is now logged in and should be redirected to onboarding
            alert('Account created successfully! Welcome to RFQ Tracker.');
            
            // Reload to trigger auth check and onboarding redirect
            window.location.href = '/';
            
        } catch (err) {
            setError(err.message || 'Failed to accept invitation');
            setAccepting(false);
        }
    };

    if (loading) {
        return (
            <div className="container mt-5">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Validating invitation...</p>
                </div>
            </div>
        );
    }

    if (error && !invitation) {
        return (
            <div className="container mt-5">
                <div className="card">
                    <div className="card-body text-center">
                        <h2>❌ Invalid Invitation</h2>
                        <p className="text-danger">{error}</p>
                        <p className="text-muted">
                            This invitation link may have expired or already been used.
                        </p>
                        <button 
                            onClick={() => navigate('/')} 
                            className="btn btn-primary"
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-body">
                            <h2 className="card-title text-center mb-4">
                                Welcome to RFQ Tracker!
                            </h2>

                            {invitation && (
                                <div className="alert alert-info mb-4">
                                    <strong>You've been invited to join:</strong>
                                    <p className="mb-1"><strong>{invitation.company_name}</strong></p>
                                    <p className="mb-1">Role: <strong>{invitation.role}</strong></p>
                                    {invitation.assigned_region && (
                                        <p className="mb-0">Region: <strong>{invitation.assigned_region}</strong></p>
                                    )}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label htmlFor="email" className="form-label">
                                        Email
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={invitation?.email || ''}
                                        disabled
                                        style={{ backgroundColor: '#f0f0f0' }}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="username" className="form-label">
                                        Username <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="username"
                                        className="form-control"
                                        placeholder="Choose a username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        required
                                        autoFocus
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="password" className="form-label">
                                        Password <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        className="form-control"
                                        placeholder="At least 8 characters"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="confirmPassword" className="form-label">
                                        Confirm Password <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        className="form-control"
                                        placeholder="Re-enter password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="btn btn-primary w-100"
                                    disabled={accepting || !username || !password || !confirmPassword}
                                >
                                    {accepting ? 'Creating Account...' : 'Accept Invitation & Create Account'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;

